import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import { MapPin, Calendar, Search, Anchor } from 'lucide-react';
import AsyncSelect from 'react-select/async';
import { useSelector } from 'react-redux';
import { pickup_icon, pickup_icon5 } from '../../../assets/images';
import debounce from 'lodash.debounce';
import Flatpickr from "react-flatpickr";

const SearchForm = ({ onSearch, tabName }) => {
    const flatpickrRef = useRef(null);
    const { instantRateLocation, airLocation } = useSelector((state) => state.instantRate);
    const [showDropdown, setShowDropdown] = useState(false);
    const dropdownRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            const path = event.composedPath ? event.composedPath() : [];

            if (dropdownRef.current && !path.includes(dropdownRef.current)) {
                setShowDropdown(false);
            }
        };

        document.addEventListener("click", handleClickOutside, true);
        return () => {
            document.removeEventListener("click", handleClickOutside, true);
        };
    }, []);

    const handleIconClick = () => {
        if (flatpickrRef.current) {
            flatpickrRef.current.flatpickr.open(); // Trigger date picker open
        }
    };

    const locationOptions = useMemo(() => {
        // Choose location data based on tabName
        const locationData = tabName === 'route' ? instantRateLocation : airLocation;

        // Handle empty or undefined data
        if (!locationData || !Array.isArray(locationData) || locationData.length === 0) {
            return [];
        }

        // Map the selected location data to options format based on tab type
        try {
            if (tabName === 'route') {
                return locationData.map(location => ({
                    value: location.id?.toString() || '',
                    label: `${location.code && location.code !== 'NA' ? `${location.code}-` : ''}${location.name || ''}`,
                    locationType: location.locationType || '',
                    locationCode: location.code || ''
                }));
            } else {
                // For Air locations, handle different data structure
                return locationData.map(location => {
                    // Check if the location already has the right format
                    if (location.label && (location.value || location.id)) {
                        return {
                            value: location.id?.toString() || '',
                            label: location.label || '',
                            id: location.id?.toString() || (location.value?.toString() || ''),
                            locationCode: location.code || ''
                        };
                    } else {
                        // Fallback to a similar format as FCL if structure is different
                        return {
                            value: location.id?.toString() || '',
                            label: `${location.code && location.code !== 'NA' ? `${location.code}-` : ''}${location.name || location.label || ''}`,
                            id: location.id?.toString() || '',
                            locationCode: location.code || ''
                        };
                    }
                });
            }
        } catch (error) {
            console.error('Error mapping location options:', error);
            return [];
        }
    }, [instantRateLocation, airLocation, tabName]);

    const memoizedLocationOptions = useMemo(() => {
        return locationOptions.map(option => ({
            ...option,
            searchLabel: option.label.toLowerCase() // Pre-compute lowercase for faster filtering
        }));
    }, [locationOptions]);

    const loadPolOptions = useCallback(
        debounce((inputValue, callback) => {
            const selectedPOD = onSearch.values.location_to?.value;
            const searchTerm = inputValue.toLowerCase();

            // Early return for empty input to show all options
            if (!searchTerm) {
                const topOptions = memoizedLocationOptions
                    .slice(0, 100) // Limit to first 100 options for better performance
                    .map(option => ({
                        ...option,
                        isDisabled: option.value === selectedPOD
                    }));
                callback(topOptions);
                return;
            }

            // Use more efficient filtering approach
            const filtered = memoizedLocationOptions
                .filter(option => option.searchLabel.includes(searchTerm))
                .slice(0, 100) // Limit results for better performance
                .map(option => ({
                    ...option,
                    isDisabled: option.value === selectedPOD
                }));

            callback(filtered);
        }, 500), // Increased debounce time for better performance
        [memoizedLocationOptions, onSearch.values.location_to?.value]
    );


    const loadPodOptions = useCallback(
        debounce((inputValue, callback) => {
            const selectedPOL = onSearch.values.location_from?.value;
            const searchTerm = inputValue.toLowerCase();

            // Early return for empty input to show all options
            if (!searchTerm) {
                const topOptions = memoizedLocationOptions
                    .slice(0, 100) // Limit to first 100 options for better performance
                    .map(option => ({
                        ...option,
                        isDisabled: option.value === selectedPOL
                    }));
                callback(topOptions);
                return;
            }

            // Use more efficient filtering approach
            const filtered = memoizedLocationOptions
                .filter(option => option.searchLabel.includes(searchTerm))
                .slice(0, 100) // Limit results for better performance
                .map(option => ({
                    ...option,
                    isDisabled: option.value === selectedPOL
                }));

            callback(filtered);
        }, 500), // Increased debounce time for better performance
        [memoizedLocationOptions, onSearch.values.location_from?.value]
    );

    const handleSubmit = (e) => {
        e.preventDefault();
    };

    return (
        <div className="bg-white p-3 rounded-lg shadow-sm mb-3">
            <form onSubmit={handleSubmit}>
                <div className="row g-3 align-items-center">
                    <div className="col-md-3 position-relative">
                        <div className="d-flex align-items-center">
                            <span className="me-2">
                                <Anchor size={18} className="text-primary" />
                            </span>
                            <div className="flex-grow-1">
                                <AsyncSelect
                                    key="pol-select"
                                    id="location_from"
                                    name="location_from"
                                    cacheOptions
                                    defaultOptions
                                    isClearable
                                    loadOptions={loadPolOptions}
                                    value={onSearch.values.location_from || null}
                                    onChange={(option) => {
                                        onSearch.setFieldValue("location_from", option);
                                    }}
                                    onBlur={() => onSearch.setFieldTouched("location_from", true)}
                                    placeholder="Type to search POL..."
                                    formatOptionLabel={({ label, locationType, isDisabled }, { context }) =>
                                        context === 'menu' ? (
                                            <div className="d-flex align-items-center">
                                            <span className="me-1">
                                                <img src={locationType === 'CITY' ? pickup_icon5 : pickup_icon} alt="Location" />
                                            </span>
                                                <span style={{ opacity: isDisabled ? 0.4 : 1 }}>{label}</span>
                                            </div>
                                        ) : <span style={{ opacity: isDisabled ? 0.4 : 1 }}>{label}</span>
                                    }
                                    classNamePrefix="form-select"
                                    styles={{
                                        control: (baseStyles, state) => ({
                                            ...baseStyles,
                                            backgroundColor: 'white',
                                            borderColor: !onSearch.errors.location_to ? 'grey' : 'red',
                                            cursor: 'default',
                                        }),
                                        // Add performance optimizations for menu rendering
                                        menuList: base => ({
                                            ...base,
                                            height: '200px', // Limit height for better performance
                                        }),
                                        option: (base, state) => ({
                                            ...base,
                                            padding: '8px 12px', // Slightly more compact options
                                            cursor: 'pointer',
                                        }),
                                        singleValue: (base) => ({
                                            ...base,
                                            color: 'black', // 👈 your custom text color
                                        }),
                                        placeholder: (base) => ({
                                            ...base,
                                            color: '#aaa', // optional placeholder color
                                        })
                                    }}
                                    // Performance optimizations
                                    filterOption={null} // Disable client-side filtering (we do it in loadOptions)
                                    isSearchable={true}
                                    backspaceRemovesValue={true}
                                    blurInputOnSelect={true}
                                    closeMenuOnSelect={true}
                                    menuShouldBlockScroll={false}
                                    menuPlacement="auto"
                                    menuPosition="fixed"
                                    maxMenuHeight={300}
                                    loadingMessage={() => "Loading locations..."}
                                    noOptionsMessage={() => "No matching locations found"}
                                />
                                {onSearch.touched.location_from &&
                                    onSearch.errors.location_from && (
                                        <div className="text-danger small">
                                            {onSearch.errors.location_from.label ||
                                                onSearch.errors.location_from}
                                        </div>
                                    )}
                            </div>
                        </div>
                    </div>

                    <div className="col-md-1 text-center">
                        <div className="d-flex justify-content-center">
                            <span className="d-inline-block">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M7 8L17 16M17 8L7 16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </span>
                        </div>
                    </div>

                    <div className="col-md-3 position-relative">
                        <div className="d-flex align-items-center">
                            <span className="me-2">
                            <Anchor size={18} className="text-primary" />
                            </span>
                            <div className="flex-grow-1">
                                <AsyncSelect
                                    key="pod-select"
                                    id="location_to"
                                    name="location_to"
                                    cacheOptions
                                    defaultOptions
                                    isClearable
                                    loadOptions={loadPodOptions}
                                    value={onSearch.values.location_to || null}
                                    onChange={(option) => onSearch.setFieldValue("location_to", option)}
                                    onBlur={() => onSearch.setFieldTouched("location_to", true)}
                                    placeholder="Type to search POD..."
                                    formatOptionLabel={({ label, locationType, isDisabled }, { context }) =>
                                        context === 'menu' ? (
                                            <div className="d-flex align-items-center">
                                            <span className="me-1">
                                                <img src={locationType === 'CITY' ? pickup_icon5 : pickup_icon} alt="Location" />
                                            </span>
                                                <span style={{ opacity: isDisabled ? 0.4 : 1 }}>{label}</span>
                                            </div>
                                        ) : <span style={{ opacity: isDisabled ? 0.4 : 1 }}>{label}</span>
                                    }
                                    classNamePrefix="form-select"
                                    styles={{
                                        control: (baseStyles, state) => ({
                                            ...baseStyles,
                                            backgroundColor:  'white',
                                            borderColor: !onSearch.errors.location_to ? 'grey' : 'red',
                                            cursor: 'default',
                                        }),
                                        // Add performance optimizations for menu rendering
                                        menuList: base => ({
                                            ...base,
                                            height: '200px', // Limit height for better performance
                                        }),
                                        option: (base, state) => ({
                                            ...base,
                                            padding: '8px 12px', // Slightly more compact options
                                            cursor: 'pointer',
                                        }),
                                        singleValue: (base) => ({
                                            ...base,
                                            color: 'black', // 👈 your custom text color
                                        }),
                                        placeholder: (base) => ({
                                            ...base,
                                            color: '#aaa', // optional placeholder color
                                        })
                                    }}
                                    // Performance optimizations
                                    filterOption={null} // Disable client-side filtering (we do it in loadOptions)
                                    isSearchable={true}
                                    backspaceRemovesValue={true}
                                    blurInputOnSelect={true}
                                    closeMenuOnSelect={true}
                                    menuShouldBlockScroll={false}
                                    menuPlacement="auto"
                                    menuPosition="fixed"
                                    maxMenuHeight={300}
                                    loadingMessage={() => "Loading locations..."}
                                    noOptionsMessage={() => "No matching locations found"}
                                />
                                {onSearch.touched.location_to &&
                                    onSearch.errors.location_to && (
                                        <div className="text-danger small">
                                            {onSearch.errors.location_to.label ||
                                                onSearch.errors.location_to}
                                        </div>
                                    )}
                            </div>
                        </div>
                    </div>

                    <div className="col-md-2">
                        <div className="input-group">
                            <span className="input-group-text bg-white border-end-0" onClick={handleIconClick}>
                                <Calendar size={18} className="text-primary" />
                            </span>
                            <Flatpickr
                                name="searchDate"
                                ref={flatpickrRef}
                                value={onSearch?.values?.searchDate || ''}
                                className={onSearch?.errors?.searchDate ? 'form-control d-block error' : 'form-control d-block'}
                                placeholder="Select Date"
                                options={{
                                    altFormat: "F j, Y",
                                    dateFormat: "Y-m-d",
                                    minDate: "today"
                                }}
                                onChange={(selectDate) => {
                                    const formatted = selectDate.map(d => {
                                        const year = d.getFullYear();
                                        const month = String(d.getMonth() + 1).padStart(2, '0');
                                        const day = String(d.getDate()).padStart(2, '0');
                                        return `${year}-${month}-${day}`;
                                    });
                                    onSearch.setFieldValue("searchDate", formatted[0]);
                                }}
                                tabIndex={0}
                            />
                        </div>
                    </div>
                    <div className="col-md-2">
                        <div style={{ maxWidth: "400px" }}>
                            <div
                                className="position-relative d-inline-block"
                                ref={dropdownRef}  // <-- wrap both button and dropdown here
                            >
                                {/* Toggle Button */}
                                <button
                                    className="btn btn-outline-primary d-flex align-items-center"
                                    onClick={() => setShowDropdown(!showDropdown)}
                                >
                                    Other parameters
                                </button>

                                {/* Dropdown Panel */}
                                {showDropdown && (
                                    <div
                                        className="bg-white border rounded-3 shadow p-3 position-absolute"
                                        style={{
                                            top: "100%",
                                            left: "0",
                                            marginTop: "8px",
                                            zIndex: 1000,
                                            minWidth: "250px",
                                        }}
                                    >
                                        {/* Weeks Ahead */}
                                        <div className="mb-3">
                                            <label className="form-label fw-bold small">WEEKS AHEAD</label>
                                            <select className="form-select" defaultValue="2 weeks">
                                                <option>1 week</option>
                                                <option>2 weeks</option>
                                                <option>3 weeks</option>
                                                <option>4 weeks</option>
                                            </select>
                                        </div>

                                        {/* Search On */}
                                        <div className="mb-3">
                                            <label className="form-label fw-bold small d-block">SEARCH ON</label>
                                            <div className="form-check form-check-inline">
                                                <input
                                                    className="form-check-input"
                                                    type="radio"
                                                    name="searchOn"
                                                    id="departure"
                                                    defaultChecked
                                                />
                                                <label className="form-check-label" htmlFor="departure">
                                                    Departure
                                                </label>
                                            </div>
                                            <div className="form-check form-check-inline">
                                                <input className="form-check-input" type="radio" name="searchOn" id="arrival" />
                                                <label className="form-check-label" htmlFor="arrival">
                                                    Arrival
                                                </label>
                                            </div>
                                        </div>

                                        {/* Shipping Lines */}
                                        <div className="mb-3">
                                            <label className="form-label fw-bold small">SHIPPING LINES</label>
                                            <select className="form-select" defaultValue="All shipping lines">
                                                <option>All shipping lines</option>
                                                <option>Maersk</option>
                                                <option>MSC</option>
                                                <option>CMA CGM</option>
                                            </select>
                                        </div>

                                        {/* Checkboxes */}
                                        <div className="form-check mb-2">
                                            <input className="form-check-input" type="checkbox" id="directService" />
                                            <label className="form-check-label" htmlFor="directService">
                                                Only direct service
                                            </label>
                                        </div>
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="alternativeOptions"
                                                defaultChecked
                                            />
                                            <label className="form-check-label" htmlFor="alternativeOptions">
                                                Show alternative options
                                            </label>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    <div className="col-md-1">
                        <button className="btn btn-primary w-100" type="button" onClick={onSearch.handleSubmit}>
                            <Search size={18} />
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
};

export default SearchForm;
